import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import { useCurrency } from "../../context/CurrencyContext";
import { COUNTRIES, getCountryByCode } from "../../utils/countryCurrencyData";
import { MultiSelect } from "../../components/ui/MultiSelect";
import { useStore } from "context/StoreContext";

interface Order {
 _id: string;
 orderNumber: string;
 user?: {
  _id: string;
  name: string;
  email: string;
 };
 email: string;
 status: "pending" | "confirmed" | "processing" | "shipped" | "delivered" | "cancelled" | "refunded";
 total: number;
 currency: string;
 payment?: {
  _id: string;
  status: string;
  method: string;
 };
 items: Array<{
  product: {
   title: string;
  };
  quantity: number;
  price: number;
 }>;
 shippingAddress: {
  firstName: string;
  lastName: string;
  address1: string;
  city: string;
 };
  trackingNumber?: string;
  location?: string; // Country code
  createdAt: string;
}

export const Orders: React.FC = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const { api } = useApi();
 const { location: adminLocation } = useCurrency();
 const { isPluginEnabled } = useStore();
 const isCarrierPluginEnabled = isPluginEnabled("shipping-carrier-integration");
 const [orders, setOrders] = useState<Order[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [search, setSearch] = useState("");
 const [status, setStatus] = useState("");
 const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
 const [initialLoad, setInitialLoad] = useState(true);
 const [availableLocations, setAvailableLocations] = useState<Array<{ code: string; name: string }>>([]);
 const [page, setPage] = useState(1);
 const [total, setTotal] = useState(0);
 const [limit] = useState(20);
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkActionLoading, setBulkActionLoading] = useState(false);
 const [exportingLabels, setExportingLabels] = useState(false);
 const searchInputRef = useRef<HTMLInputElement>(null);
 const wasSearchFocusedRef = useRef(false);

 // Fetch store's available locations
 useEffect(() => {
  const fetchStoreLocations = async () => {
   try {
    const response = await api.get("/locations/admin?isActive=true");
    const locations = response.data.locations || [];
    // Map locations to country codes and names
    const locationOptions = locations.map((loc: any) => {
     const country = getCountryByCode(loc.countryCode);
     return {
      code: loc.countryCode,
      name: country?.name || loc.country || loc.countryCode,
     };
    });
    setAvailableLocations(locationOptions);
    
    // Auto-filter by admin's country on initial load if it's in available locations
    if (initialLoad && adminLocation?.countryCode) {
     const adminCountryInLocations = locationOptions.some(
      (loc: { code: string }) => loc.code === adminLocation.countryCode
     );
     if (adminCountryInLocations) {
      setSelectedLocations([adminLocation.countryCode]);
     }
     setInitialLoad(false);
    }
   } catch (err: any) {
    console.error("Error fetching store locations:", err);
    // On error, fallback to all countries
    setAvailableLocations(
     COUNTRIES.map((country) => ({
      code: country.code,
      name: country.name,
     }))
    );
   }
  };
  
  fetchStoreLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 useEffect(() => {
  fetchOrders();
 }, [page, search, status, selectedLocations]);

 // Maintain focus on search input
 useEffect(() => {
  if (wasSearchFocusedRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
   const cursorPosition = searchInputRef.current.selectionStart || search.length;
   searchInputRef.current.focus();
   searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }
 }, [search]);

 const fetchOrders = async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(status && { status }),
    ...(search && { search: search }),
    ...(selectedLocations.length > 0 && { location: selectedLocations.join(",") }),
    // Request auto-location detection only on initial load when no location is selected
    ...(initialLoad && selectedLocations.length === 0 && adminLocation?.countryCode && { autoLocation: "true" }),
   });
   
   const response = await api.get(`/orders/admin?${params.toString()}`);
   setOrders(response.data.orders || []);
   setTotal(response.data.pagination?.total || 0);
   setInitialLoad(false); // Mark as no longer initial load after first fetch
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.orders.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const handleStatusChange = async (orderId: string, newStatus: string) => {
  try {
   await api.put(`/orders/${orderId}/status`, { status: newStatus });
   setOrders(
    orders.map((o) =>
     o._id === orderId ? { ...o, status: newStatus as any } : o
    )
   );
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.orders.errors.updateStatus"));
  }
 };

 const getStatusColor = (status: string) => {
  switch (status) {
   case "delivered":
    return "bg-green-100 text-green-800";
   case "shipped":
    return "bg-blue-100 text-blue-800";
   case "processing":
    return "bg-yellow-100 text-yellow-800";
   case "pending":
    return "bg-gray-100 text-gray-800";
   case "cancelled":
    return "bg-red-100 text-red-800";
   case "confirmed":
    return "bg-blue-100 text-blue-800";
   case "refunded":
    return "bg-purple-100 text-purple-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 const handleSelectAll = (checked: boolean) => {
  if (checked) {
   setSelectedIds(new Set(orders.map((o) => o._id)));
  } else {
   setSelectedIds(new Set());
  }
 };

 const handleSelectOne = (id: string, checked: boolean) => {
  const newSelected = new Set(selectedIds);
  if (checked) {
   newSelected.add(id);
  } else {
   newSelected.delete(id);
  }
  setSelectedIds(newSelected);
 };

 const handleBulkStatusChange = async (newStatus: string) => {
  if (selectedIds.size === 0) return;

  try {
   setBulkActionLoading(true);
   setError(null);
   await api.post("/orders/admin/bulk/status", { ids: Array.from(selectedIds), status: newStatus });
   setSelectedIds(new Set());
   fetchOrders();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.orders.errors.updateStatus"));
  } finally {
   setBulkActionLoading(false);
  }
 };

 const handleBulkExportShippingLabels = async () => {
  if (selectedIds.size === 0) return;

  try {
   setExportingLabels(true);
   setError(null);
   
   const response = await api.post(
    "/orders/admin/bulk/shipping-labels",
    { orderIds: Array.from(selectedIds) },
    { responseType: "blob" }
   );

   // Create a blob URL and trigger download
   const blob = new Blob([response.data], { type: "application/pdf" });
   const url = window.URL.createObjectURL(blob);
   const link = document.createElement("a");
   link.href = url;
   link.setAttribute("download", `shipping-labels-${new Date().toISOString().split("T")[0]}.pdf`);
   document.body.appendChild(link);
   link.click();
   link.remove();
   window.URL.revokeObjectURL(url);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.orders.errors.exportLabels") || "Failed to export shipping labels");
  } finally {
   setExportingLabels(false);
  }
 };

 const handleBulkAction = async (action: string) => {
  if (selectedIds.size === 0) return;

  if (action === "") return; // Placeholder selected, do nothing

  if (action === "clear") {
   setSelectedIds(new Set());
   return;
  }

  if (action === "delete") {
   if (!window.confirm(t("admin.orders.confirmBulkDelete", { count: selectedIds.size }))) {
    return;
   }
   try {
    setBulkActionLoading(true);
    setError(null);
    await api.post("/orders/admin/bulk/delete", { ids: Array.from(selectedIds) });
    setSelectedIds(new Set());
    fetchOrders();
   } catch (err: any) {
    setError(err.response?.data?.error || t("admin.orders.errors.delete"));
   } finally {
    setBulkActionLoading(false);
   }
   return;
  }

  if (action === "exportLabels") {
   if (!window.confirm(t("admin.orders.confirmBulkExportLabels", { count: selectedIds.size }) || `Export shipping labels for ${selectedIds.size} order(s)?`)) {
    return;
   }
   await handleBulkExportShippingLabels();
   return;
  }

  // Status changes: pending, confirmed, processing, shipped, delivered, cancelled, refunded
  if (["pending", "confirmed", "processing", "shipped", "delivered", "cancelled", "refunded"].includes(action)) {
   const statusKey = `setTo${action.charAt(0).toUpperCase() + action.slice(1)}`;
   const statusLabel = t(`admin.orders.${statusKey}`) || action;
   if (!window.confirm(t("admin.orders.confirmBulkStatusChange", { count: selectedIds.size, status: statusLabel }) || `Change status to "${statusLabel}" for ${selectedIds.size} order(s)?`)) {
    return;
   }
   await handleBulkStatusChange(action);
   return;
  }
 };

 const totalPages = Math.ceil(total / limit);

 if (loading && orders.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{t("admin.orders.title")}</Heading>

    {error && <Alert variant="error">{error}</Alert>}

    <Card padding="lg">
     <Stack spacing="md">
      {selectedIds.size > 0 && (
       <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 ">
        <div className="text-sm font-medium text-blue-900">
         {t("admin.orders.selectedCount", { count: selectedIds.size })}
        </div>
        <div className="flex gap-2 items-center">
         <select
          onChange={(e) => {
           const value = e.target.value;
           handleBulkAction(value);
           // Reset select to placeholder after action
           e.target.value = "";
          }}
          disabled={bulkActionLoading || exportingLabels}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white min-w-[180px]"
          defaultValue=""
         >
          <option value="" disabled>{t("admin.orders.selectBulkAction") || "Select action..."}</option>
          {isCarrierPluginEnabled && (
           <option value="exportLabels">{t("admin.orders.exportLabels") || "Export Labels"}</option>
          )}
          <option value="" disabled>--- {t("admin.orders.changeStatus")} ---</option>
          <option value="pending">{t("admin.orders.setToPending")}</option>
          <option value="confirmed">{t("admin.orders.setToConfirmed")}</option>
          <option value="processing">{t("admin.orders.setToProcessing")}</option>
          <option value="shipped">{t("admin.orders.setToShipped")}</option>
          <option value="delivered">{t("admin.orders.setToDelivered")}</option>
          <option value="cancelled">{t("admin.orders.setToCancelled")}</option>
          <option value="refunded">{t("admin.orders.setToRefunded")}</option>
          <option value="" disabled>--- {t("admin.orders.otherActions") || "Other Actions"} ---</option>
          <option value="delete" className="text-red-600">{t("admin.orders.deleteSelected")}</option>
          <option value="clear">{t("admin.orders.clearSelection")}</option>
         </select>
         {(bulkActionLoading || exportingLabels) && (
          <Spinner size="sm" />
         )}
        </div>
       </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
       <Input
        ref={searchInputRef}
        placeholder={t("admin.orders.searchPlaceholder")}
        value={search}
        onChange={(e) => {
         setSearch(e.target.value);
         setPage(1);
        }}
        onFocus={() => { wasSearchFocusedRef.current = true; }}
        onBlur={() => {
         setTimeout(() => {
          if (document.activeElement !== searchInputRef.current) {
           wasSearchFocusedRef.current = false;
          }
         }, 0);
        }}
       />
       <Select
        options={[
         { value: "", label: t("admin.orders.allStatuses") },
         { value: "pending", label: t("orders.pending") },
         { value: "confirmed", label: t("admin.orders.confirmed") },
         { value: "processing", label: t("orders.processing") },
         { value: "shipped", label: t("orders.shipped") },
         { value: "delivered", label: t("orders.delivered") },
         { value: "cancelled", label: t("orders.cancelled") },
         { value: "refunded", label: t("admin.orders.refunded") },
        ]}
        value={status}
        placeholder={t("admin.orders.allStatuses")}
        onChange={(e) => {
         setStatus(e.target.value);
         setPage(1);
        }}
       />
       <MultiSelect
        options={availableLocations.map((location) => ({
         value: location.code,
         label: `${location.name} (${location.code})`,
        }))}
        value={selectedLocations}
        onChange={(values) => {
         setSelectedLocations(values);
         setPage(1);
        }}
        placeholder={t("admin.orders.selectLocations") || "Select countries..."}
        helperText={
         availableLocations.length === 0
          ? t("admin.orders.noLocationsAvailable") || "No locations available for this store"
          : selectedLocations.length === 0
          ? t("admin.orders.selectLocationsHelper") || "Select one or more countries to filter orders"
          : undefined
        }
        disabled={availableLocations.length === 0}
       />
      </div>

      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           <input
            type="checkbox"
            checked={selectedIds.size > 0 && selectedIds.size === orders.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-gray-300"
           />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.orderNumber")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.customer")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.items")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.total")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.payment")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.status")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.location") || "Location"}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.date")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.actions")}
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {orders.length === 0 ? (
          <tr>
           <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
            {t("admin.orders.noOrders")}
           </td>
          </tr>
         ) : (
         orders.map((order) => (
          <tr key={order._id}>
           <td className="px-6 py-4 whitespace-nowrap">
            <input
             type="checkbox"
             checked={selectedIds.has(order._id)}
             onChange={(e) => handleSelectOne(order._id, e.target.checked)}
             className="rounded border-gray-300"
            />
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <button
             onClick={() => navigate(`/admin/orders/${order._id}`)}
             className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
            >
             {order.orderNumber}
            </button>
           </td>
           <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm font-medium text-gray-900">
             {order.user?.name || t("admin.orders.guest")}
            </div>
            <div className="text-xs text-gray-500">{order.email}</div>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {t("admin.orders.itemsCount", { count: order.items.length })}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {order.currency || "$"}{order.total.toFixed(2)}
           </td>
           <td className="px-6 py-4 whitespace-nowrap">
            {order.payment ? (
             <div>
              <span className={`px-2 py-1 text-xs font-semibold ${
               order.payment.status === "completed" ? "bg-green-100 text-green-800" :
               order.payment.status === "authorized" ? "bg-blue-100 text-blue-800" :
               order.payment.status === "pending" ? "bg-yellow-100 text-yellow-800" :
               order.payment.status === "processing" ? "bg-blue-100 text-blue-800" :
               order.payment.status === "failed" ? "bg-red-100 text-red-800" :
               order.payment.status === "refunded" ? "bg-purple-100 text-purple-800" :
               "bg-gray-100 text-gray-800"
              }`}>
               {t(`admin.orders.paymentStatus.${order.payment.status}`) || order.payment.status}
              </span>
              <div className="text-xs text-gray-500 mt-1">
               {t(`admin.orders.paymentMethod.${order.payment.method}`) || order.payment.method}
              </div>
             </div>
            ) : (
             <span className="text-xs text-gray-400">{t("admin.orders.noPayment")}</span>
            )}
           </td>
           <td className="px-6 py-4 whitespace-nowrap">
            <select
             value={order.status}
             onChange={(e) =>
              handleStatusChange(order._id, e.target.value)
             }
             className={`px-2 py-1 text-xs font-semibold border border-gray-300 ${getStatusColor(
              order.status
             )}`}
            >
             <option value="pending">{t("orders.pending")}</option>
             <option value="confirmed">{t("admin.orders.confirmed")}</option>
             <option value="processing">{t("orders.processing")}</option>
             <option value="shipped">{t("orders.shipped")}</option>
             <option value="delivered">{t("orders.delivered")}</option>
             <option value="cancelled">{t("orders.cancelled")}</option>
             <option value="refunded">{t("admin.orders.refunded")}</option>
            </select>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {order.location ? (
             <span>
              {getCountryByCode(order.location)?.name || order.location}
             </span>
            ) : (
             <span className="text-gray-400">-</span>
            )}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {new Date(order.createdAt).toLocaleDateString()}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm">
            <Button
             variant="outline"
             size="sm"
             onClick={() => navigate(`/admin/orders/${order._id}`)}
            >
             {t("admin.orders.view")}
            </Button>
           </td>
          </tr>
         )))}
        </tbody>
       </table>
      </div>

      {totalPages > 1 && (
       <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
         {t("admin.orders.showing", { start: ((page - 1) * limit) + 1, end: Math.min(page * limit, total), total })}
        </div>
        <div className="flex gap-2">
         <Button
          variant="outline"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
         >
          {t("common.previous")}
         </Button>
         <div className="flex items-center gap-2">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
           const pageNum = i + 1;
           return (
            <Button
             key={pageNum}
             variant={page === pageNum ? "primary" : "outline"}
             size="sm"
             onClick={() => setPage(pageNum)}
            >
             {pageNum}
            </Button>
           );
          })}
         </div>
         <Button
          variant="outline"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
         >
          {t("common.next")}
         </Button>
        </div>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default Orders;
